import React from 'react';
import { useTranslation } from 'react-i18next';
import Immutable from 'immutable';

import { getProductDiscountTiers } from '../../containers/Cart/helpers';
import findActiveTier from '../DiscountTierAccordion/helpers';
import { formatPrice } from '../../helpers/commerce';

import './style.scss';

const rangeLabel = (tier, t) => (tier.max_qty !== null
  ? t('checkout.discount_accordion_range', {
    min: tier.min_qty,
    max: tier.max_qty,
  })
  : t('checkout.discount_accordion_min', { min: tier.min_qty }));

/**
 * Itemised breakdown of the volume discounts applied to the cart, shown in the
 * summary's "Korting" overlay. Renders one group per discounted product —
 * title + discounted amount, the active tier range, and the percentage.
 *
 * Assumes staffel discounts only; coupon codes are handled separately and (per
 * product rules) never co-occur with a staffel.
 *
 * @param {object} props - Component props.
 * @param {Immutable.List} props.products - The cart products (order items).
 * @returns {JSX.Element} The discount breakdown list.
 */
const DiscountSummaryList = ({ products = Immutable.List() }) => {
  const { t } = useTranslation();

  const items = products
    .map((product) => {
      const quantity = parseInt(
        product.getIn(['attributes', 'quantity'], 1),
        10,
      );
      const activeTier = findActiveTier(
        getProductDiscountTiers(product),
        quantity,
      );
      if (!activeTier) {
        return null;
      }
      const unitPrice = parseFloat(
        product.getIn(['attributes', 'unit_price', 'number'], 0),
      );
      return {
        id: product.get('id'),
        title: product.getIn(
          [
            'relationships',
            'purchased_entity',
            'relationships',
            'product_id',
            'attributes',
            'title',
          ],
          '',
        ),
        // Volume discounts are a percentage, so derive the amount from the tier.
        amount: unitPrice * quantity * (activeTier.percentage / 100),
        activeTier,
      };
    })
    .filter(Boolean)
    .toArray();

  return (
    <ul className="DiscountSummaryList">
      {items.map((item) => (
        <li key={item.id} className="DiscountSummaryList__item">
          <div className="DiscountSummaryList__row">
            <span className="DiscountSummaryList__title">{item.title}</span>
            <span className="DiscountSummaryList__amount">
              {`-${t('cart.price_display', {
                price: formatPrice(item.amount),
                interpolation: { escapeValue: true },
              })}`}
            </span>
          </div>
          <div className="DiscountSummaryList__tier">
            {t('cart.discount_overlay_tier', {
              range: rangeLabel(item.activeTier, t),
            })}
          </div>
          <div className="DiscountSummaryList__percentage">
            {t('cart.discount_overlay_percentage', {
              percentage: item.activeTier.percentage,
            })}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default DiscountSummaryList;
